import type { DeployState } from './workspace/deploy-action-state';
import type { WorkspaceHeaderMode } from './workspace/workspace-view-types';

const GITHUB_REPO_FULL_NAME =
  /^([A-Za-z0-9](?:[A-Za-z0-9-]{0,98}[A-Za-z0-9])?)\/([A-Za-z0-9._-]{1,100})$/;

const WORKSPACE_MODE_LABEL: Record<WorkspaceHeaderMode, string> = {
  chat: 'Develop',
  plan: 'Plan',
  preview: 'Preview',
  design: 'Design',
  protohub: 'ProtoHub',
  art: 'Art',
  craft: 'Craft',
};

export interface GameBugReportInput {
  repoFullName: string;
  gameId: string;
  gameName: string;
  foundryVersion?: string;
  gameVersion?: string;
  branch?: string;
  placement?: 'local' | 'cloud';
  surface: 'desktop' | 'cloud';
  workspaceMode: WorkspaceHeaderMode;
  deployState: DeployState;
  /**
   * #3500 / hw#2080 — when the report is opened from a deploy FAILURE
   * (the banner's "Report this problem"), the failing step + captured
   * error pre-fill the report so the user doesn't have to transcribe
   * them. Absent for the generic header "Report a bug" path.
   */
  failingStep?: string;
  errorDetail?: string;
}

export interface GameBugReportResult {
  url: string;
  body: string;
}

function cleanSingleLine(value: string | undefined, maxLength = 160): string | undefined {
  if (!value) return undefined;
  const cleaned = value.replace(/\s+/g, ' ').trim();
  return cleaned ? cleaned.slice(0, maxLength) : undefined;
}

function inlineCode(value: string | undefined): string | undefined {
  const cleaned = cleanSingleLine(value);
  return cleaned ? `\`${cleaned.replace(/`/g, "'")}\`` : undefined;
}

function contextLine(label: string, value: string | undefined): string | undefined {
  return value ? `- ${label}: ${value}` : undefined;
}

/**
 * Build a reviewable GitHub issue form for a bug in the current game.
 *
 * The repository identifier is deliberately strict because it becomes part
 * of the navigation target. Context values are single-line and bounded before
 * entering markdown. No logs, diagnostics, filesystem paths, or credentials
 * are collected; the reporter sees the entire pre-filled body before GitHub
 * submits it under their own identity.
 */
export function buildGameBugReportUrl(input: GameBugReportInput): GameBugReportResult | null {
  const repoMatch = GITHUB_REPO_FULL_NAME.exec(input.repoFullName);
  if (!repoMatch) return null;
  const [, owner, repo] = repoMatch;
  if (repo === '.' || repo === '..') return null;

  const gameName = cleanSingleLine(input.gameName, 80) ?? cleanSingleLine(input.gameId, 80);
  const title = `Bug report: ${gameName ?? 'Foundry game'}`;
  const context = [
    contextLine(
      'Game',
      `${inlineCode(input.gameName) ?? '`Unknown`'} (${inlineCode(input.gameId) ?? '`Unknown`'})`,
    ),
    contextLine('Repository', inlineCode(input.repoFullName)),
    contextLine('Foundry version', inlineCode(input.foundryVersion)),
    contextLine('Game version', inlineCode(input.gameVersion)),
    contextLine('Git branch', inlineCode(input.branch)),
    contextLine('Surface', inlineCode(input.surface === 'desktop' ? 'Desktop' : 'Cloud')),
    contextLine(
      'Project placement',
      input.placement ? inlineCode(input.placement === 'local' ? 'This Mac' : 'Cloud') : undefined,
    ),
    contextLine('Workspace', inlineCode(WORKSPACE_MODE_LABEL[input.workspaceMode])),
    contextLine('Deploy state', inlineCode(input.deployState)),
    // #3500 / hw#2080 — deploy-failure pre-fill (only present when
    // reporting from the failure banner's "Report this problem").
    contextLine('Failing step', inlineCode(input.failingStep)),
    contextLine('Error', inlineCode(input.errorDetail)),
  ].filter((line): line is string => Boolean(line));

  // #3500 / hw#2080 — when opened from a deploy failure, pre-fill
  // "What happened" with the failing step so the report is actionable
  // out of the box.
  const whatHappened = input.failingStep
    ? `Sending this game to the TV failed at the "${cleanSingleLine(input.failingStep, 80)}" step.`
    : '_Describe the problem and what you were doing when it appeared._';

  const body = [
    '## What happened',
    '',
    whatHappened,
    '',
    '## Steps to reproduce',
    '',
    '1. ',
    '',
    '## Expected behavior',
    '',
    '_What did you expect to happen?_',
    '',
    '## Foundry context',
    '',
    ...context,
    '',
    '> No logs or diagnostics were attached automatically. Review this context before submitting.',
  ].join('\n');

  const url = new URL(`https://github.com/${owner}/${repo}/issues/new`);
  url.searchParams.set('title', title);
  url.searchParams.set('body', body);
  return { url: url.toString(), body };
}
